import React, { useCallback, useEffect, useMemo, useState } from "react";
import { gql, useMutation } from "@apollo/client";
import {
  fetchEventImages,
  fetchEventImageActivities,
} from "../../../api/eventImages";
import {
  updateImageRecord,
  deleteImageRecord,
  runThumbnailBackfill,
} from "./photoApi";
import { useAuth } from "../../../context/AuthContext";
import { ADMIN_EMAILS } from "../../../auth/adminEmails";
import ReassignOwnerModal from "./ReassignOwnerModal";
import "./PhotoManagerTab.css";

// Phase A.3 reassignment for images. Empty string clears ownership.
const SET_EVENT_IMAGE_CREATED_BY = gql`
  mutation SetEventImageCreatedBy($s3Key: ID!, $createdBy: String!) {
    setEventImageCreatedBy(s3Key: $s3Key, createdBy: $createdBy)
  }
`;

const S3_BASE = "https://s3.amazonaws.com/social-images-public/";
const ALL_FILTER = "All";

// Build a small-thumbnail URL by swapping the image extension for `_thumb.jpg`.
// Backend backfill writes a 320px max-dimension JPEG sibling for every photo;
// until backfill completes, many keys won't have one — callers MUST attach
// `handleThumbErrorFallback` so the original S3 URL is used instead.
const THUMB_SUFFIX_RE = /\.(jpe?g|png|webp|gif)$/i;
const buildThumbUrl = (s3Key) => {
  if (!s3Key) return null;
  return `${S3_BASE}${s3Key.replace(THUMB_SUFFIX_RE, "_thumb.jpg")}`;
};
const buildOriginalUrl = (s3Key) => `${S3_BASE}${s3Key}`;
// onError handler that swaps to the original URL exactly once. The
// `data-fallback-tried` flag prevents an infinite loop if the original is
// also broken (browser retries onError on each failed src change).
const handleThumbErrorFallback = (e, originalSrc) => {
  if (!e || !e.target) return;
  if (e.target.dataset.fallbackTried === "1") return;
  e.target.dataset.fallbackTried = "1";
  e.target.src = originalSrc;
};

const formatBytes = (bytes) => {
  if (bytes == null || Number.isNaN(Number(bytes))) return "—";
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const formatRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 48) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const month = Math.round(day / 30);
  if (month < 18) return `${month}mo ago`;
  const yr = Math.round(day / 365);
  return `${yr}y ago`;
};

const formatAbsolute = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

// Convert any ISO 8601 string (or "YYYY-MM-DD...") to "YYYY-MM-DD" for the
// date input. Uses local-time components so what the user sees matches the
// pre-filled value when editing.
const ymd = (iso) => {
  if (!iso) return "";
  // If it looks like "YYYY-MM-DD..." just slice to avoid timezone drift.
  if (typeof iso === "string" && /^\d{4}-\d{2}-\d{2}/.test(iso)) {
    return iso.slice(0, 10);
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const getDisplayName = (item) =>
  item?.displayName || item?.alias || item?.s3Key || "";

// Effective sort timestamp: takenAt if present, else uploadedAt.
const effectiveTime = (item) => {
  const v = item?.takenAt || item?.uploadedAt;
  if (!v) return 0;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? 0 : t;
};

const compareTakenAtDesc = (a, b) => effectiveTime(b) - effectiveTime(a);

// Favorites first, then takenAt desc. Used inside per-activity groups so the
// favorites bubble to the top within each section.
const compareFavoritesThenTakenAtDesc = (a, b) => {
  const af = a?.favorite ? 1 : 0;
  const bf = b?.favorite ? 1 : 0;
  if (af !== bf) return bf - af;
  return effectiveTime(b) - effectiveTime(a);
};

// Custom section ordering for both list and grid views. Listed activities come
// first in this order; everything else falls to the end alphabetically.
const ACTIVITY_PRIORITY = [
  "volleyball",
  "pickleball",
  "dance",
  "run",
  "other",
  "climb",
];
const ACTIVITY_PRIORITY_INDEX = new Map(
  ACTIVITY_PRIORITY.map((a, i) => [a, i])
);
const compareActivityPriority = (a, b) => {
  const ai = ACTIVITY_PRIORITY_INDEX.has(a)
    ? ACTIVITY_PRIORITY_INDEX.get(a)
    : Infinity;
  const bi = ACTIVITY_PRIORITY_INDEX.has(b)
    ? ACTIVITY_PRIORITY_INDEX.get(b)
    : Infinity;
  if (ai !== bi) return ai - bi;
  return String(a).localeCompare(String(b));
};

const PhotoManagerTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activityFilter, setActivityFilter] = useState(ALL_FILTER);

  // Activity options for the inline edit dropdown.
  const [activityOptions, setActivityOptions] = useState([]);

  // Inline edit state — tracks all three editable fields at once.
  const [editingKey, setEditingKey] = useState(null);
  const [editDraft, setEditDraft] = useState({
    displayName: "",
    activity: "",
    takenDate: "",
  });
  const [savingKey, setSavingKey] = useState(null);
  const [rowError, setRowError] = useState({}); // s3Key -> message

  const [previewKeys, setPreviewKeys] = useState(() => new Set());

  const [deleteTarget, setDeleteTarget] = useState(null); // item or null
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // Phase A.3 reassignment. Super-admin only; captains see no button.
  const [reassignTarget, setReassignTarget] = useState(null);
  const [setEventImageCreatedBy] = useMutation(SET_EVENT_IMAGE_CREATED_BY);
  const { userInfo, userRole } = useAuth();
  const isAdminUser =
    (userInfo?.email &&
      ADMIN_EMAILS.includes(userInfo.email.toLowerCase())) ||
    userRole === "admin";

  const handleReassignPicked = async (newOwnerEmail) => {
    if (!reassignTarget) return;
    try {
      await setEventImageCreatedBy({
        variables: {
          s3Key: reassignTarget.s3Key,
          createdBy: newOwnerEmail || "",
        },
      });
      // Mirror the new owner into the local item so the title attribute
      // and any future check reflects the change without a full refetch.
      const newCreatedBy = newOwnerEmail || null;
      setItems((prev) =>
        prev.map((it) =>
          it.s3Key === reassignTarget.s3Key
            ? { ...it, createdBy: newCreatedBy }
            : it
        )
      );
      setReassignTarget(null);
    } catch (err) {
      window.alert(`Reassign failed: ${err.message || err}`);
    }
  };
  const [fadingKeys, setFadingKeys] = useState(() => new Set());

  const [toast, setToast] = useState(null); // {kind:'error'|'success', message}

  // View-mode toggle: 'grid' is the default per user request; 'list' is the
  // alternative table view.
  const [viewMode, setViewMode] = useState("grid");
  // Grid grouping. Off by default — newest-photos-first flat grid is the
  // most common case ("what's been uploaded recently?"). Admins can opt
  // into per-activity sections via the toggle in the grid header.
  const [groupByActivity, setGroupByActivity] = useState(false);
  // Favorites-first sort. Off by default — the host usually just wants
  // newest at the top, regardless of star status. Tick the checkbox to
  // bubble favorited photos above non-favorites within each bucket.
  const [favoritesFirst, setFavoritesFirst] = useState(false);
  // Category-selection modal state. `categorySelection` holds the current
  // applied selection and persists across toggles within the same mount.
  // null = not yet initialized (we'll auto-fill on first activation).
  const [categorySelection, setCategorySelection] = useState(null);
  // Draft selection while the modal is open (so Cancel discards changes).
  const [categoryDraft, setCategoryDraft] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  // True iff this is the first time we're opening the modal as part of
  // switching to grid mode — Cancel in that case should NOT switch view.
  const [pendingGridActivation, setPendingGridActivation] = useState(false);

  // ---- Grid in-place edit modal state ----------------------------------
  // `gridEditKey` is the s3Key of the item being edited from grid view.
  // We use a SEPARATE draft state from the list-view inline edit so the two
  // flows don't interfere with each other (clicking a grid cell shouldn't
  // also leave a list-view inline editor open if the user toggles views).
  const [gridEditKey, setGridEditKey] = useState(null);
  const [gridEditDraft, setGridEditDraft] = useState({
    displayName: "",
    activity: "",
    takenDate: "",
    favorite: false,
  });
  const [gridEditSaving, setGridEditSaving] = useState(false);
  const [gridEditError, setGridEditError] = useState("");

  // ---- Thumbnail regeneration -----------------------------------------
  const [regenerating, setRegenerating] = useState(false);

  const showToast = useCallback((kind, message) => {
    setToast({ kind, message });
    window.setTimeout(() => setToast(null), 4000);
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchEventImages({});
      setItems(Array.isArray(resp?.items) ? resp.items : []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("PhotoManagerTab load error:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Escape closes the grid-edit modal (but not while saving — would be jarring
  // mid-PATCH).
  useEffect(() => {
    if (!gridEditKey) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !gridEditSaving) {
        setGridEditKey(null);
        setGridEditError("");
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [gridEditKey, gridEditSaving]);

  // Fetch activity options once for the inline-edit dropdown.
  useEffect(() => {
    let cancelled = false;
    fetchEventImageActivities()
      .then((resp) => {
        if (cancelled) return;
        const acts = Array.isArray(resp?.activities) ? resp.activities : [];
        const sorted = [...acts].sort((a, b) =>
          String(a).localeCompare(String(b))
        );
        setActivityOptions(sorted);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("PhotoManagerTab activities load failed:", err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const allActivities = useMemo(() => {
    const set = new Set();
    items.forEach((it) => {
      if (it && it.activity) set.add(it.activity);
    });
    return [...set].sort((a, b) => String(a).localeCompare(String(b)));
  }, [items]);

  // Auto-initialize categorySelection with all activities checked once items
  // arrive — so the default Grid view loads without popping the modal. Admins
  // can still narrow via the "Change categories" button.
  useEffect(() => {
    if (categorySelection || allActivities.length === 0) return;
    const activities = {};
    allActivities.forEach((a) => {
      activities[a] = true;
    });
    setCategorySelection({ allChecked: true, favoritesOnly: false, activities });
  }, [allActivities, categorySelection]);

  const filteredItems = useMemo(() => {
    if (activityFilter === ALL_FILTER) return items;
    return items.filter((it) => (it?.activity || "") === activityFilter);
  }, [items, activityFilter]);

  // Group by activity for the "All" view; sort newest-first within each group
  // using takenAt (falling back to uploadedAt). Section order = activity asc.
  const grouped = useMemo(() => {
    const map = new Map();
    filteredItems.forEach((it) => {
      const act = it?.activity || "Other";
      if (!map.has(act)) map.set(act, []);
      map.get(act).push(it);
    });
    const cmp = favoritesFirst
      ? compareFavoritesThenTakenAtDesc
      : compareTakenAtDesc;
    map.forEach((arr) => arr.sort(cmp));
    const sections = [...map.entries()].sort((a, b) =>
      compareActivityPriority(a[0], b[0])
    );
    return sections;
  }, [filteredItems, favoritesFirst]);

  // Activity options shown in the edit dropdown — union of fetched activities
  // and whatever we've seen on existing rows, plus the row's own current
  // activity (covers legacy values that don't appear in either list).
  const activityChoicesFor = (item) => {
    const set = new Set();
    activityOptions.forEach((a) => set.add(a));
    allActivities.forEach((a) => set.add(a));
    if (item?.activity) set.add(item.activity);
    return [...set].sort((a, b) => String(a).localeCompare(String(b)));
  };

  const startEdit = (item) => {
    setEditingKey(item.s3Key);
    setEditDraft({
      displayName: getDisplayName(item),
      activity: item.activity || "",
      takenDate: ymd(item.takenAt) || ymd(item.uploadedAt) || "",
    });
    setRowError((prev) => {
      const next = { ...prev };
      delete next[item.s3Key];
      return next;
    });
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditDraft({ displayName: "", activity: "", takenDate: "" });
  };

  const saveEdit = async (item) => {
    const trimmedName = (editDraft.displayName || "").trim();
    if (!trimmedName) {
      setRowError((prev) => ({
        ...prev,
        [item.s3Key]: "Display name cannot be empty.",
      }));
      return;
    }
    const newActivity = (editDraft.activity || "").trim();
    if (!newActivity) {
      setRowError((prev) => ({
        ...prev,
        [item.s3Key]: "Activity cannot be empty.",
      }));
      return;
    }
    const newTakenDate = (editDraft.takenDate || "").trim(); // "YYYY-MM-DD" or ""

    // Compute deltas vs. original row.
    const origName = getDisplayName(item);
    const origActivity = item.activity || "";
    const origTakenDate = ymd(item.takenAt);

    const body = {};
    if (trimmedName !== origName) body.displayName = trimmedName;
    if (newActivity !== origActivity) body.activity = newActivity;
    if (newTakenDate && newTakenDate !== origTakenDate) {
      body.takenAt = `${newTakenDate}T00:00:00`;
    }

    if (Object.keys(body).length === 0) {
      cancelEdit();
      return;
    }

    // Optimistic update — apply the diffs locally now.
    const prevItems = items;
    setItems((curr) =>
      curr.map((it) => {
        if (it.s3Key !== item.s3Key) return it;
        const next = { ...it };
        if (body.displayName !== undefined) next.displayName = body.displayName;
        if (body.activity !== undefined) next.activity = body.activity;
        if (body.takenAt !== undefined) next.takenAt = body.takenAt;
        return next;
      })
    );
    setSavingKey(item.s3Key);

    try {
      const resp = await updateImageRecord(item.s3Key, body);
      if (resp && resp.item) {
        setItems((curr) =>
          curr.map((it) => (it.s3Key === item.s3Key ? resp.item : it))
        );
      }
      setEditingKey(null);
      setEditDraft({ displayName: "", activity: "", takenDate: "" });
      showToast("success", "Photo updated.");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Update failed:", err);
      setItems(prevItems); // revert
      showToast("error", err && err.message ? err.message : "Update failed.");
      setRowError((prev) => ({
        ...prev,
        [item.s3Key]: err && err.message ? err.message : "Update failed.",
      }));
    } finally {
      setSavingKey(null);
    }
  };

  const toggleFavorite = async (item) => {
    const key = item.s3Key;
    const next = !item.favorite;
    const prevItems = items;
    // Optimistic flip
    setItems((curr) =>
      curr.map((it) => (it.s3Key === key ? { ...it, favorite: next } : it))
    );
    try {
      const resp = await updateImageRecord(key, { favorite: next });
      if (resp && resp.item) {
        setItems((curr) =>
          curr.map((it) => (it.s3Key === key ? resp.item : it))
        );
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Toggle favorite failed:", err);
      setItems(prevItems);
      showToast(
        "error",
        err && err.message ? err.message : "Could not update favorite."
      );
    }
  };

  const togglePreview = (s3Key) => {
    setPreviewKeys((prev) => {
      const next = new Set(prev);
      if (next.has(s3Key)) next.delete(s3Key);
      else next.add(s3Key);
      return next;
    });
  };

  const openDelete = (item) => {
    setDeleteTarget(item);
    setDeleteText("");
    setDeleteError("");
  };

  const closeDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteText("");
    setDeleteError("");
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if ((deleteText || "").trim().toLowerCase() !== "delete") {
      setDeleteError("Type 'delete' to confirm.");
      return;
    }
    setDeleting(true);
    setDeleteError("");
    try {
      await deleteImageRecord(deleteTarget.s3Key);
      const key = deleteTarget.s3Key;
      // Trigger fade
      setFadingKeys((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      setDeleteTarget(null);
      setDeleteText("");
      // If the grid-edit modal was open on this same item, close it too so
      // the user isn't staring at an edit form for a row that's about to
      // disappear.
      if (gridEditKey === key) {
        setGridEditKey(null);
        setGridEditError("");
        setGridEditDraft({
          displayName: "",
          activity: "",
          takenDate: "",
          favorite: false,
        });
      }
      // After fade, refresh list
      window.setTimeout(() => {
        setFadingKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
        loadItems();
      }, 450);
      showToast("success", "Photo deleted.");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Delete failed:", err);
      setDeleteError(err && err.message ? err.message : "Delete failed.");
    } finally {
      setDeleting(false);
    }
  };

  // ---- Grid view + category selector ----------------------------------

  const openCategoryModal = (isFirstActivation) => {
    // If we have no prior selection, default to all activities checked +
    // favoritesOnly off + allChecked true.
    const base = categorySelection || {
      allChecked: true,
      favoritesOnly: false,
      activities: allActivities.reduce((acc, a) => {
        acc[a] = true;
        return acc;
      }, {}),
    };
    // Make sure the draft includes any newly discovered activities.
    const activities = { ...base.activities };
    allActivities.forEach((a) => {
      if (!(a in activities)) activities[a] = base.allChecked;
    });
    setCategoryDraft({
      allChecked: base.allChecked,
      favoritesOnly: !!base.favoritesOnly,
      activities,
    });
    setPendingGridActivation(!!isFirstActivation);
    setShowCategoryModal(true);
  };

  const cancelCategoryModal = () => {
    setShowCategoryModal(false);
    setCategoryDraft(null);
    // If this was a first-time activation, do NOT switch to grid view.
    if (pendingGridActivation) {
      setViewMode("list");
    }
    setPendingGridActivation(false);
  };

  const applyCategoryModal = () => {
    if (!categoryDraft) {
      setShowCategoryModal(false);
      setPendingGridActivation(false);
      return;
    }
    setCategorySelection({
      allChecked: categoryDraft.allChecked,
      favoritesOnly: categoryDraft.favoritesOnly,
      activities: { ...categoryDraft.activities },
    });
    setShowCategoryModal(false);
    setCategoryDraft(null);
    setPendingGridActivation(false);
  };

  const handleViewModeChange = (next) => {
    if (next === viewMode) return;
    if (next === "grid") {
      // First time switching to grid this session? open the modal.
      if (!categorySelection) {
        setViewMode("grid");
        openCategoryModal(true);
        return;
      }
      setViewMode("grid");
      return;
    }
    setViewMode("list");
  };

  const toggleDraftActivity = (activity) => {
    setCategoryDraft((d) => {
      if (!d) return d;
      const nextActivities = { ...d.activities, [activity]: !d.activities[activity] };
      // If any individual is unchecked while All was on, All becomes off.
      const allOn = Object.values(nextActivities).every(Boolean);
      return {
        ...d,
        activities: nextActivities,
        allChecked: allOn,
      };
    });
  };

  const toggleDraftAll = () => {
    setCategoryDraft((d) => {
      if (!d) return d;
      const nextAll = !d.allChecked;
      const nextActivities = {};
      Object.keys(d.activities).forEach((a) => {
        nextActivities[a] = nextAll;
      });
      return {
        ...d,
        allChecked: nextAll,
        activities: nextActivities,
      };
    });
  };

  const toggleDraftFavoritesOnly = () => {
    setCategoryDraft((d) => (d ? { ...d, favoritesOnly: !d.favoritesOnly } : d));
  };

  // Items to show in the grid based on the persisted category selection.
  const gridItems = useMemo(() => {
    if (viewMode !== "grid") return [];
    if (!categorySelection) return [];
    let pool = items;
    if (categorySelection.favoritesOnly) {
      pool = pool.filter((it) => !!it && !!it.favorite);
    } else {
      pool = pool.filter((it) => {
        if (!it) return false;
        const act = it.activity || "";
        if (!act) return false;
        return !!categorySelection.activities[act];
      });
    }
    return [...pool].sort(
      favoritesFirst
        ? compareFavoritesThenTakenAtDesc
        : compareTakenAtDesc
    );
  }, [items, categorySelection, viewMode, favoritesFirst]);

  // Sections for the grid view. Per-activity headers when not in favorites-only
  // mode; a single synthetic "★ Favorites" section when favoritesOnly is on.
  // Items inside each section preserve gridItems' favorites-first sort.
  const gridSections = useMemo(() => {
    if (viewMode !== "grid") return [];
    if (!categorySelection) return [];
    if (categorySelection.favoritesOnly) {
      return gridItems.length > 0
        ? [{ key: "__favorites__", label: "★ Favorites", items: gridItems }]
        : [];
    }
    const map = new Map();
    for (const it of gridItems) {
      const act = it.activity || "Other";
      if (!map.has(act)) map.set(act, []);
      map.get(act).push(it);
    }
    return [...map.entries()]
      .sort(([a], [b]) => compareActivityPriority(a, b))
      .map(([activity, secItems]) => ({
        key: activity,
        label: activity,
        items: secItems,
      }));
  }, [gridItems, categorySelection, viewMode]);

  const selectedCategoryCount = useMemo(() => {
    if (!categorySelection) return 0;
    if (categorySelection.favoritesOnly) return 1; // "Favorites" treated as one bucket
    return Object.values(categorySelection.activities).filter(Boolean).length;
  }, [categorySelection]);

  // Open the in-place grid edit modal. Stays in Grid view (unlike the old
  // bounce-to-list flow) so the user keeps their place in the grid.
  const openGridCellEdit = (item) => {
    setGridEditKey(item.s3Key);
    setGridEditDraft({
      displayName: getDisplayName(item),
      activity: item.activity || "",
      takenDate: ymd(item.takenAt) || ymd(item.uploadedAt) || "",
      favorite: !!item.favorite,
    });
    setGridEditError("");
  };

  const closeGridEdit = () => {
    if (gridEditSaving) return;
    setGridEditKey(null);
    setGridEditError("");
    setGridEditDraft({
      displayName: "",
      activity: "",
      takenDate: "",
      favorite: false,
    });
  };

  // Save handler for the grid-edit modal. Mirrors `saveEdit` but writes to
  // the modal's own draft + error state so it's independent of list-view
  // inline edit.
  const saveGridEdit = async () => {
    if (!gridEditKey) return;
    const item = items.find((it) => it.s3Key === gridEditKey);
    if (!item) {
      closeGridEdit();
      return;
    }
    const trimmedName = (gridEditDraft.displayName || "").trim();
    if (!trimmedName) {
      setGridEditError("Display name cannot be empty.");
      return;
    }
    const newActivity = (gridEditDraft.activity || "").trim();
    if (!newActivity) {
      setGridEditError("Activity cannot be empty.");
      return;
    }
    const newTakenDate = (gridEditDraft.takenDate || "").trim();

    const origName = getDisplayName(item);
    const origActivity = item.activity || "";
    const origTakenDate = ymd(item.takenAt);
    const origFavorite = !!item.favorite;

    const body = {};
    if (trimmedName !== origName) body.displayName = trimmedName;
    if (newActivity !== origActivity) body.activity = newActivity;
    if (newTakenDate && newTakenDate !== origTakenDate) {
      body.takenAt = `${newTakenDate}T00:00:00`;
    }
    if (!!gridEditDraft.favorite !== origFavorite) {
      body.favorite = !!gridEditDraft.favorite;
    }

    if (Object.keys(body).length === 0) {
      closeGridEdit();
      return;
    }

    const prevItems = items;
    setItems((curr) =>
      curr.map((it) => {
        if (it.s3Key !== item.s3Key) return it;
        const next = { ...it };
        if (body.displayName !== undefined) next.displayName = body.displayName;
        if (body.activity !== undefined) next.activity = body.activity;
        if (body.takenAt !== undefined) next.takenAt = body.takenAt;
        if (body.favorite !== undefined) next.favorite = body.favorite;
        return next;
      })
    );
    setGridEditSaving(true);
    setGridEditError("");

    try {
      const resp = await updateImageRecord(item.s3Key, body);
      if (resp && resp.item) {
        setItems((curr) =>
          curr.map((it) => (it.s3Key === item.s3Key ? resp.item : it))
        );
      }
      showToast("success", "Photo updated.");
      setGridEditKey(null);
      setGridEditDraft({
        displayName: "",
        activity: "",
        takenDate: "",
        favorite: false,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Update failed:", err);
      setItems(prevItems); // revert
      const msg = err && err.message ? err.message : "Update failed.";
      setGridEditError(msg);
      showToast("error", msg);
    } finally {
      setGridEditSaving(false);
    }
  };

  const handleRegenerateThumbnails = async () => {
    if (regenerating) return;
    setRegenerating(true);
    showToast("info", "Generating thumbnails…");
    try {
      const resp = await runThumbnailBackfill();
      const generated = (resp && resp.generated) || 0;
      const skipped = (resp && resp.skipped) || 0;
      setToast({
        kind: "success",
        message: `Generated ${generated} thumbnails (skipped ${skipped} already-existing).`,
      });
      window.setTimeout(() => setToast(null), 10000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Thumbnail backfill failed:", err);
      showToast(
        "error",
        err && err.message ? err.message : "Thumbnail backfill failed."
      );
    } finally {
      setRegenerating(false);
    }
  };

  const renderRow = (item) => {
    const key = item.s3Key;
    const isEditing = editingKey === key;
    const isSaving = savingKey === key;
    const isPreview = previewKeys.has(key);
    const isFading = fadingKeys.has(key);
    const rowErr = rowError[key];
    const choices = isEditing ? activityChoicesFor(item) : null;

    return (
      <React.Fragment key={key}>
        <tr
          className={isFading ? "photo-manager-tab__row--fading" : ""}
        >
          <td className="photo-manager-tab__star-cell">
            <button
              type="button"
              className={`photo-manager-tab__star${
                item.favorite ? " photo-manager-tab__star--on" : ""
              }`}
              onClick={() => toggleFavorite(item)}
              aria-label={item.favorite ? "Unfavorite" : "Favorite"}
              aria-pressed={!!item.favorite}
              title={item.favorite ? "Unfavorite" : "Favorite"}
            >
              {item.favorite ? "★" : "☆"}
            </button>
          </td>
          <td>
            {isEditing ? (
              <>
                <input
                  type="text"
                  className="photo-manager-tab__name-input"
                  value={editDraft.displayName}
                  onChange={(e) =>
                    setEditDraft((d) => ({
                      ...d,
                      displayName: e.target.value,
                    }))
                  }
                  disabled={isSaving}
                  autoFocus
                />
                {rowErr && (
                  <div className="photo-manager-tab__inline-error">
                    {rowErr}
                  </div>
                )}
              </>
            ) : (
              getDisplayName(item)
            )}
          </td>
          <td>
            {isEditing ? (
              <select
                className="photo-manager-tab__activity-select"
                value={editDraft.activity}
                onChange={(e) =>
                  setEditDraft((d) => ({ ...d, activity: e.target.value }))
                }
                disabled={isSaving}
              >
                {choices.length === 0 ? (
                  <option value="">(no activities)</option>
                ) : (
                  choices.map((act) => (
                    <option key={act} value={act}>
                      {act}
                    </option>
                  ))
                )}
              </select>
            ) : (
              <span className="photo-manager-tab__pill">
                {item.activity || "—"}
              </span>
            )}
          </td>
          <td title={formatAbsolute(item.uploadedAt)}>
            {formatRelative(item.uploadedAt)}
          </td>
          <td title={formatAbsolute(item.takenAt)}>
            {isEditing ? (
              <input
                type="date"
                className="photo-manager-tab__date-input"
                value={editDraft.takenDate}
                onChange={(e) =>
                  setEditDraft((d) => ({ ...d, takenDate: e.target.value }))
                }
                disabled={isSaving}
              />
            ) : item.takenAt ? (
              ymd(item.takenAt)
            ) : (
              "—"
            )}
          </td>
          <td>{formatBytes(item.sizeBytes)}</td>
          <td>
            <span className="photo-manager-tab__s3key" title={key}>
              {key}
            </span>
          </td>
          <td>
            <div className="photo-manager-tab__actions">
              {isEditing ? (
                <>
                  <button
                    type="button"
                    className="photo-manager-tab__btn photo-manager-tab__btn--primary"
                    onClick={() => saveEdit(item)}
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving…" : "Save"}
                  </button>
                  <button
                    type="button"
                    className="photo-manager-tab__btn"
                    onClick={cancelEdit}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="photo-manager-tab__btn"
                    onClick={() => startEdit(item)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="photo-manager-tab__btn photo-manager-tab__btn--ghost"
                    onClick={() => togglePreview(key)}
                  >
                    {isPreview ? "Hide preview" : "Show preview"}
                  </button>
                  {isAdminUser && (
                    <button
                      type="button"
                      className="photo-manager-tab__btn"
                      onClick={() => setReassignTarget(item)}
                      title={
                        item.createdBy
                          ? `Owner: ${item.createdBy}`
                          : "No recorded owner"
                      }
                    >
                      Reassign
                    </button>
                  )}
                  <button
                    type="button"
                    className="photo-manager-tab__btn photo-manager-tab__btn--danger"
                    onClick={() => openDelete(item)}
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          </td>
        </tr>
        {isPreview && (
          <tr className={isFading ? "photo-manager-tab__row--fading" : ""}>
            <td colSpan={8}>
              <div className="photo-manager-tab__preview">
                <img
                  src={buildThumbUrl(key)}
                  onError={(e) =>
                    handleThumbErrorFallback(e, buildOriginalUrl(key))
                  }
                  alt={getDisplayName(item)}
                  loading="lazy"
                />
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  const renderTable = (rows) => (
    <div className="photo-manager-tab__table-wrap">
      <table className="photo-manager-tab__table">
        <thead>
          <tr>
            <th aria-label="Favorite" className="photo-manager-tab__star-cell">
              ★
            </th>
            <th>Display Name</th>
            <th>Activity</th>
            <th>Uploaded</th>
            <th>Taken</th>
            <th>Size</th>
            <th>s3Key</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>{rows.map(renderRow)}</tbody>
      </table>
    </div>
  );

  return (
    <div className="photo-manager-tab">
      <div className="photo-manager-tab__header">
        <h2>Photos</h2>
        <div className="photo-manager-tab__filter">
          <label htmlFor="photo-manager-activity">Activity:</label>
          <select
            id="photo-manager-activity"
            value={activityFilter}
            onChange={(e) => setActivityFilter(e.target.value)}
            disabled={viewMode === "grid"}
          >
            <option value={ALL_FILTER}>All</option>
            {allActivities.map((act) => (
              <option key={act} value={act}>
                {act}
              </option>
            ))}
          </select>
          <span className="photo-manager-tab__count">
            ({filteredItems.length})
          </span>
          <div
            className="photo-manager-tab__viewtoggle"
            role="group"
            aria-label="View mode"
          >
            <button
              type="button"
              className={`photo-manager-tab__viewtoggle-btn${
                viewMode === "list"
                  ? " photo-manager-tab__viewtoggle-btn--active"
                  : ""
              }`}
              onClick={() => handleViewModeChange("list")}
              aria-pressed={viewMode === "list"}
            >
              List
            </button>
            <button
              type="button"
              className={`photo-manager-tab__viewtoggle-btn${
                viewMode === "grid"
                  ? " photo-manager-tab__viewtoggle-btn--active"
                  : ""
              }`}
              onClick={() => handleViewModeChange("grid")}
              aria-pressed={viewMode === "grid"}
            >
              Grid
            </button>
          </div>
          <button
            type="button"
            className="photo-manager-tab__btn"
            onClick={loadItems}
            disabled={loading}
          >
            Refresh
          </button>
          <button
            type="button"
            className="photo-manager-tab__btn"
            onClick={handleRegenerateThumbnails}
            disabled={regenerating}
            title="Generate _thumb.jpg files for any photos missing one."
          >
            {regenerating ? "Generating thumbnails…" : "Regenerate thumbnails"}
          </button>
        </div>
      </div>

      {toast && (
        <div
          className={`photo-manager-tab__status photo-manager-tab__status--${toast.kind}`}
        >
          {toast.message}
        </div>
      )}

      {loading && (
        <div className="photo-manager-tab__status photo-manager-tab__status--info">
          Loading photos…
        </div>
      )}

      {error && !loading && (
        <div className="photo-manager-tab__status photo-manager-tab__status--error">
          Could not load photos: {error.message || "unknown error"}
        </div>
      )}

      {!loading && !error && viewMode === "list" && filteredItems.length === 0 && (
        <div className="photo-manager-tab__status photo-manager-tab__status--info">
          No photos to show.
        </div>
      )}

      {!loading && !error && viewMode === "list" && filteredItems.length > 0 && (
        <>
          {activityFilter === ALL_FILTER ? (
            grouped.map(([activity, rows]) => (
              <div key={activity}>
                <div className="photo-manager-tab__section-header">
                  {activity} ({rows.length})
                </div>
                {renderTable(rows)}
              </div>
            ))
          ) : (
            renderTable(
              [...filteredItems].sort(
                favoritesFirst
                  ? compareFavoritesThenTakenAtDesc
                  : compareTakenAtDesc
              )
            )
          )}
        </>
      )}

      {!loading && !error && viewMode === "grid" && (
        <>
          <div className="photo-manager-tab__grid-header">
            <div>
              Showing {gridItems.length} photo{gridItems.length === 1 ? "" : "s"}{" "}
              across {selectedCategoryCount} categor
              {selectedCategoryCount === 1 ? "y" : "ies"}
              {categorySelection && categorySelection.favoritesOnly
                ? " (favorites only)"
                : ""}
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={groupByActivity}
                  onChange={(e) => setGroupByActivity(e.target.checked)}
                  disabled={
                    !!(categorySelection && categorySelection.favoritesOnly)
                  }
                />
                Group by activity
              </label>
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={favoritesFirst}
                  onChange={(e) => setFavoritesFirst(e.target.checked)}
                />
                Favorites first
              </label>
              <button
                type="button"
                className="photo-manager-tab__btn"
                onClick={() => openCategoryModal(false)}
              >
                Change categories
              </button>
            </div>
          </div>
          {gridItems.length === 0 ? (
            <div className="photo-manager-tab__status photo-manager-tab__status--info">
              No photos in the selected categories.{" "}
              <button
                type="button"
                className="photo-manager-tab__btn"
                onClick={() => openCategoryModal(false)}
                style={{ marginLeft: 8 }}
              >
                Change categories
              </button>
            </div>
          ) : (() => {
            // Reusable cell renderer to keep flat / grouped branches in sync.
            const renderCell = (item) => (
              <button
                key={item.s3Key}
                type="button"
                className="photo-manager-tab__grid-cell"
                onClick={() => openGridCellEdit(item)}
                title={getDisplayName(item)}
              >
                <img
                  className="photo-manager-tab__grid-thumb"
                  src={buildThumbUrl(item.s3Key)}
                  onError={(e) =>
                    handleThumbErrorFallback(e, buildOriginalUrl(item.s3Key))
                  }
                  alt={getDisplayName(item)}
                  loading="lazy"
                />
                <div className="photo-manager-tab__grid-caption">
                  <span className="photo-manager-tab__grid-name">
                    {getDisplayName(item)}
                  </span>
                  {item.favorite && (
                    <span className="photo-manager-tab__grid-fav" aria-hidden>
                      ★
                    </span>
                  )}
                </div>
              </button>
            );

            // Favorites-only mode is already a single virtual section, so
            // the grouped path renders it correctly as one block. The flat
            // path collapses the section header and shows just the items.
            const showSections =
              groupByActivity ||
              (categorySelection && categorySelection.favoritesOnly);

            if (!showSections) {
              return (
                <div className="photo-manager-tab__grid">
                  {gridItems.map(renderCell)}
                </div>
              );
            }
            return gridSections.map((section) => (
              <div
                key={section.key}
                className="photo-manager-tab__grid-section"
              >
                <h4 className="photo-manager-tab__grid-section-header">
                  {section.label}{" "}
                  <span className="photo-manager-tab__grid-section-count">
                    ({section.items.length})
                  </span>
                </h4>
                <div className="photo-manager-tab__grid">
                  {section.items.map(renderCell)}
                </div>
              </div>
            ));
          })()}
        </>
      )}

      {showCategoryModal && categoryDraft && (
        <div
          className="photo-manager-tab__modal-backdrop"
          onClick={cancelCategoryModal}
        >
          <div
            className="photo-manager-tab__modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Choose categories to display in the grid</h3>
            <label className="photo-manager-tab__cat-row">
              <input
                type="checkbox"
                checked={!!categoryDraft.allChecked}
                onChange={toggleDraftAll}
              />
              <strong>All</strong>
            </label>
            <label className="photo-manager-tab__cat-row">
              <input
                type="checkbox"
                checked={!!categoryDraft.favoritesOnly}
                onChange={toggleDraftFavoritesOnly}
              />
              <span>★ Favorites only</span>
            </label>
            <div className="photo-manager-tab__cat-list">
              {Object.keys(categoryDraft.activities)
                .sort((a, b) => String(a).localeCompare(String(b)))
                .map((act) => (
                  <label key={act} className="photo-manager-tab__cat-row">
                    <input
                      type="checkbox"
                      checked={!!categoryDraft.activities[act]}
                      onChange={() => toggleDraftActivity(act)}
                      disabled={!!categoryDraft.favoritesOnly}
                    />
                    <span>{act}</span>
                  </label>
                ))}
              {Object.keys(categoryDraft.activities).length === 0 && (
                <div className="photo-manager-tab__inline-error">
                  No activities found in the current photos.
                </div>
              )}
            </div>
            <div className="photo-manager-tab__modal-actions">
              <button
                type="button"
                className="photo-manager-tab__btn"
                onClick={cancelCategoryModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="photo-manager-tab__btn photo-manager-tab__btn--primary"
                onClick={applyCategoryModal}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {gridEditKey && (() => {
        const item = items.find((it) => it.s3Key === gridEditKey);
        if (!item) return null;
        const choices = activityChoicesFor(item);
        return (
          <div
            className="photo-manager-tab__modal-backdrop"
            onClick={closeGridEdit}
          >
            <div
              className="photo-manager-tab__modal photo-manager-tab__modal--wide"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Edit photo"
            >
              <button
                type="button"
                className="photo-manager-tab__modal-close"
                onClick={closeGridEdit}
                disabled={gridEditSaving}
                aria-label="Close"
                title="Close"
              >
                ×
              </button>
              <h3>Edit photo</h3>

              <div className="photo-manager-tab__edit-modal-body">
                <div className="photo-manager-tab__edit-modal-imgwrap">
                  <img
                    src={buildOriginalUrl(item.s3Key)}
                    alt={getDisplayName(item)}
                    className="photo-manager-tab__edit-modal-img"
                    loading="lazy"
                  />
                </div>

                <div className="photo-manager-tab__edit-modal-fields">
                  <label className="photo-manager-tab__edit-field">
                    <span className="photo-manager-tab__edit-field-label">
                      Display Name
                    </span>
                    <input
                      type="text"
                      className="photo-manager-tab__name-input"
                      value={gridEditDraft.displayName}
                      onChange={(e) =>
                        setGridEditDraft((d) => ({
                          ...d,
                          displayName: e.target.value,
                        }))
                      }
                      disabled={gridEditSaving}
                      autoFocus
                    />
                  </label>

                  <label className="photo-manager-tab__edit-field">
                    <span className="photo-manager-tab__edit-field-label">
                      Activity
                    </span>
                    <select
                      className="photo-manager-tab__activity-select"
                      value={gridEditDraft.activity}
                      onChange={(e) =>
                        setGridEditDraft((d) => ({
                          ...d,
                          activity: e.target.value,
                        }))
                      }
                      disabled={gridEditSaving}
                    >
                      {choices.length === 0 ? (
                        <option value="">(no activities)</option>
                      ) : (
                        choices.map((act) => (
                          <option key={act} value={act}>
                            {act}
                          </option>
                        ))
                      )}
                    </select>
                  </label>

                  <label className="photo-manager-tab__edit-field">
                    <span className="photo-manager-tab__edit-field-label">
                      Taken Date
                    </span>
                    <input
                      type="date"
                      className="photo-manager-tab__date-input"
                      value={gridEditDraft.takenDate}
                      onChange={(e) =>
                        setGridEditDraft((d) => ({
                          ...d,
                          takenDate: e.target.value,
                        }))
                      }
                      disabled={gridEditSaving}
                    />
                  </label>

                  <label className="photo-manager-tab__edit-field photo-manager-tab__edit-field--inline">
                    <input
                      type="checkbox"
                      checked={!!gridEditDraft.favorite}
                      onChange={(e) =>
                        setGridEditDraft((d) => ({
                          ...d,
                          favorite: e.target.checked,
                        }))
                      }
                      disabled={gridEditSaving}
                    />
                    <span className="photo-manager-tab__edit-field-label">
                      ★ Favorite
                    </span>
                  </label>

                  <div
                    className="photo-manager-tab__s3key"
                    style={{ marginTop: 4 }}
                    title={item.s3Key}
                  >
                    {item.s3Key}
                  </div>

                  {gridEditError && (
                    <div className="photo-manager-tab__inline-error">
                      {gridEditError}
                    </div>
                  )}
                </div>
              </div>

              <div className="photo-manager-tab__modal-actions photo-manager-tab__modal-actions--split">
                <div className="photo-manager-tab__modal-actions">
                  <button
                    type="button"
                    className="photo-manager-tab__btn photo-manager-tab__btn--danger"
                    onClick={() => openDelete(item)}
                    disabled={gridEditSaving}
                  >
                    Delete
                  </button>
                  {isAdminUser && (
                    <button
                      type="button"
                      className="photo-manager-tab__btn"
                      onClick={() => setReassignTarget(item)}
                      disabled={gridEditSaving}
                      title={
                        item.createdBy
                          ? `Owner: ${item.createdBy}`
                          : "No recorded owner"
                      }
                    >
                      Reassign
                    </button>
                  )}
                </div>
                <div className="photo-manager-tab__modal-actions">
                  <button
                    type="button"
                    className="photo-manager-tab__btn"
                    onClick={closeGridEdit}
                    disabled={gridEditSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="photo-manager-tab__btn photo-manager-tab__btn--primary"
                    onClick={saveGridEdit}
                    disabled={gridEditSaving}
                  >
                    {gridEditSaving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <ReassignOwnerModal
        open={!!reassignTarget}
        onClose={() => setReassignTarget(null)}
        onSelect={handleReassignPicked}
        recordLabel="image"
        recordTitle={
          reassignTarget?.displayName || reassignTarget?.s3Key || ""
        }
        currentOwner={reassignTarget?.createdBy || ""}
      />

      {deleteTarget && (
        <div
          className="photo-manager-tab__modal-backdrop"
          onClick={closeDelete}
        >
          <div
            className="photo-manager-tab__modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Delete photo?</h3>
            <div>
              <strong>{getDisplayName(deleteTarget)}</strong>
              <div
                className="photo-manager-tab__s3key"
                style={{ marginTop: 4 }}
                title={deleteTarget.s3Key}
              >
                {deleteTarget.s3Key}
              </div>
            </div>
            <div>Type <code>delete</code> to confirm:</div>
            <input
              type="text"
              className="photo-manager-tab__modal-input"
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              disabled={deleting}
              autoFocus
            />
            {deleteError && (
              <div className="photo-manager-tab__inline-error">
                {deleteError}
              </div>
            )}
            <div className="photo-manager-tab__modal-actions">
              <button
                type="button"
                className="photo-manager-tab__btn"
                onClick={closeDelete}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="photo-manager-tab__btn photo-manager-tab__btn--danger"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoManagerTab;
